/** Full horizontal bound on wide / landscape viewports. */
export const SHIP_BOUND_X = 28;
/** Portrait phones get a fraction of this so the ship stays on-screen. */
export const SHIP_BOUND_X_MIN_SCALE = 0.38;
export const SHIP_SPEED = 42;
export const STRAFE_SPEED = 38;
export const VERT_SPEED = 32;
/** Analog gyro max contribution (matches keyboard unit vector). */
export const TILT_DEADZONE = 0.1;
export const SPAWN_AHEAD = 220;
export const DESPAWN_BEHIND = 35;
export const SPAWN_INTERVAL_START = 1.15;
export const SPAWN_INTERVAL_MIN = 0.45;
export const OBSTACLE_BASE_SPEED = 55;
export const GRID_SCROLL_BOOST = 2.8;
/** When ship flies this far on -Z, rebase world so grids/camera stay precise. */
export const WORLD_REBASE_Z = -2000;

/** Starting / max missile loadout. */
export const MISSILE_MAX = 5;
/** Starting / max railgun rounds. */
export const RAILGUN_MAX = 120;
/** Starting / max chaff pods (Regular mode). */
export const CHAFF_MAX = 10;
/**
 * Sandbox: obstacle active-count slider range.
 * Typical live count is only ~4–10 (spawn interval + corridor length), so the
 * default/max stay in that band so the slider has an obvious effect.
 */
export const OBSTACLE_LIMIT_MIN = 0;
export const OBSTACLE_LIMIT_MAX = 16;
export const OBSTACLE_LIMIT_DEFAULT = 8;
/** Forward speed along -Z (into the corridor). */
export const MISSILE_SPEED = 130;
/** How aggressively hard-locked missiles steer toward the enemy (higher = snappier). */
export const MISSILE_HOMING = 7.5;
export const RAIL_SPEED = 155;
/** Despawn when this far ahead of the ship. */
export const MISSILE_RANGE = 200;
export const RAIL_RANGE = 240;
/** Minimum seconds between shots. */
export const MISSILE_FIRE_COOLDOWN = 0.28;
export const RAIL_FIRE_COOLDOWN = 0.08;
/**
 * After a full volley starts: 10 waves × 0.5s, plus a small beat so the next
 * trigger can’t overlap the previous dump.
 */
export const CHAFF_FIRE_COOLDOWN = 5.2;
/** Parallel pods per drop wave (left + right). */
export const CHAFF_PAIR_COUNT = 2;
/** Lateral spacing between the two parallel pods (world units). */
export const CHAFF_PAIR_SPACING = 4.2;
/** How many pair-drops one fire trigger releases (chaff). */
export const CHAFF_VOLLEY_WAVES = 10;
/** F-22 flares: more waves, same pair cadence. */
export const FLARE_VOLLEY_WAVES = 15;
/** Cooldown after a full flare dump (15 × 0.5s + margin). */
export const FLARE_FIRE_COOLDOWN = 7.7;
/** Seconds between each pair-drop in a volley. */
export const CHAFF_VOLLEY_INTERVAL = 0.5;
/** Flare burn lifetime (slow fall-back, no firework pop). */
export const FLARE_LIFE = 2.6;
/** Flare fall speed (world −Y). */
export const FLARE_FALL_SPEED = 14;
/** Flare drift backward (world +Z; flight is −Z). */
export const FLARE_BACK_SPEED = 16;
/** Lateral diverge speed for left/right flares. */
export const FLARE_DIVERGE_SPEED = 9;
export const FLARE_SCALE = 1.25;
/** Spawn a trail spark this often while a flare burns. */
export const FLARE_TRAIL_INTERVAL = 0.045;
/**
 * How close an enemy missile must get to a chaff pod to be destroyed there
 * (larger than mesh radii so the decoy is reliable).
 */
export const CHAFF_MISSILE_CATCH_RADIUS = 6.5;
/** World scale for fired projectiles. */
export const MISSILE_SCALE = 2.15;
export const RAIL_SCALE = 1.6;
export const CHAFF_SCALE = 1.35;
/** Chaff pod flight speed (straight ahead, −Z) after the drop. */
export const CHAFF_SPEED = 88;
/** Initial dip duration before leveling into a straight run. */
export const CHAFF_DROP_TIME = 0.2;
/** Downward speed during the drop phase (world +Y up). */
export const CHAFF_DROP_SPEED = 42;
/** Forward speed while dropping (slightly slower than cruise). */
export const CHAFF_DROP_FORWARD = 48;
/** How far below the ship the pod starts (world units). */
export const CHAFF_SPAWN_DROP = 3.8;
/** Seconds after fire before the pod pops (firecracker fuse). */
export const CHAFF_FUSE = 0.5;
/** World-unit radius of the burst that eats enemy bolts. */
export const CHAFF_BURST_RADIUS = 16;
/** Spark count for the chaff pop. */
export const CHAFF_SPARK_MIN = 10;
export const CHAFF_SPARK_MAX = 16;
export const CHAFF_SPARK_LIFE = 0.45;
/** Yellow / gold palette for chaff sparks and soft haze (no red/grey). */
export const CHAFF_SPARK_PALETTE = [
  0xfffde7, 0xfff9c4, 0xfff59d, 0xffee58, 0xffeb3b, 0xffd54f, 0xffc107, 0xffb300, 0xffc107,
] as const;
/** Soft gold haze puffs (replaces grey smoke). */
export const CHAFF_HAZE_PALETTE = [0xfff8e1, 0xffecb3, 0xffe082, 0xffd54f] as const;
/** Debris chunk count range on obstacle break. */
export const DEBRIS_MIN = 5;
export const DEBRIS_MAX = 9;
export const DEBRIS_LIFE = 0.85;
/** Pastel palette for destroyed obstacle cubes. */
export const OBSTACLE_DEBRIS_PALETTE = [
  0x80bcbd, 0xaad9bb, 0xd5f0c1, 0xf9f7c9, 0xd9edbf, 0xdcffb7, 0xffd54f,
] as const;
/** Grey/black palette matching the arrowhead interceptor. */
export const ENEMY_DEBRIS_PALETTE = [
  0x9aa0a8, 0x6a717c, 0x3e444e, 0x252a32, 0x14171c, 0x4a515c, 0xb0b6be, 0xc5c9d0,
] as const;

/** Enemy HP: 1 missile or 4 rail hits. */
export const ENEMY_MAX_HP = 4;
export const ENEMY_AHEAD = 48;
/** Max simultaneous sandbox foes. */
export const ENEMY_SPAWN_MAX = 4;
/** Depth spacing between stacked enemies (world units further ahead). */
export const ENEMY_Z_STAGGER = 14;
/** Horizon spawn distance ahead of the ship (match block spawn feel). */
export const ENEMY_HORIZON_SPAWN = 220;
export const ENEMY_APPROACH_TIME = 3.2;
export const ENEMY_HORIZON_APPROACH_TIME = 5.4;
/** Full mesh scale once in combat. */
export const ENEMY_SCALE = 1.55;
/** Horizon foes start tiny (like blocks) and grow as they close. */
export const ENEMY_HORIZON_SCALE_MIN = 0.32;
export const ENEMY_BURST_COUNT = 3;
export const ENEMY_BURST_GAP = 0.1;
/** Competitive salvo cadence (closer to ally pressure, still dodgeable). */
export const ENEMY_SHOOT_INTERVAL_MIN = 1.25;
export const ENEMY_SHOOT_INTERVAL_MAX = 2.35;
/** Hold X / long-press duration to hard-lock enemy (default brick missiles). */
export const ENEMY_LOCK_HOLD = 2.5;
/** F-22 AIM-9 Sidewinder: faster IR lock acquire. */
export const SIDEWINDER_LOCK_HOLD = 1.5;
export const ENEMY_BOLT_SPEED = 142;
/** Chance each spawned foe is missile-capable (lock → fire). */
export const ENEMY_MISSILE_SPAWN_CHANCE = 1 / 20;
/** After launching a missile, wait this long before locking again. */
export const ENEMY_MISSILE_COOLDOWN = 8;
/** Seconds the enemy must hold a lock on the player before launch. */
export const ENEMY_MISSILE_LOCK_HOLD = 3;
/** Hostile missile flight speed. */
export const ENEMY_MISSILE_SPEED = 100;
/** Steering rate toward player / chaff decoy. */
export const ENEMY_MISSILE_HOMING = 6.2;
/** Max travel distance from ship before despawn. */
export const ENEMY_MISSILE_RANGE = 240;
export const ENEMY_MISSILE_SCALE = 2.05;
/**
 * Lateral (X/Y) lag: how fast the follow point chases the player’s strafe.
 * Used as a soft bias toward the fight, not pure 1:1 copy.
 */
export const ENEMY_LATERAL_FOLLOW = 1.15;
/** How fast the enemy mesh eases onto the roam/dodge target. */
export const ENEMY_LATERAL_DAMP = 1.4;
/** Z stay-in-front tracking (keeps pace with corridor flow, not lagged). */
export const ENEMY_Z_DAMP = 7;
/** Random roam retarget — same idea as the wingman so the foe stays mobile. */
export const ENEMY_ROAM_MIN = 0.9;
export const ENEMY_ROAM_MAX = 2.0;

/** Ally wingman: a bit ahead of the player (closer than the enemy). */
export const TEAMMATE_AHEAD = 26;
/** Max simultaneous sandbox friendlies. */
export const TEAMMATE_SPAWN_MAX = 3;
/** Depth spacing between stacked allies (world units further ahead). */
export const TEAMMATE_Z_STAGGER = 10;
export const TEAMMATE_MAX_HP = 2;
export const TEAMMATE_APPROACH_TIME = 2.6;
export const TEAMMATE_SCALE = 1.6;
/** Semi-auto rail bursts — longer gaps so the ally is less spammy. */
export const TEAMMATE_SHOOT_INTERVAL_MIN = 1.75;
export const TEAMMATE_SHOOT_INTERVAL_MAX = 3.0;
export const TEAMMATE_BURST_COUNT = 3;
export const TEAMMATE_BURST_GAP = 0.1;
export const TEAMMATE_BOLT_SPEED = 148;
/** How often the wingman picks a new random roam point in the flight plane. */
export const TEAMMATE_ROAM_MIN = 1.1;
export const TEAMMATE_ROAM_MAX = 2.4;
export const TEAMMATE_LATERAL_DAMP = 1.35;
export const TEAMMATE_Z_DAMP = 6.5;
/** White/red/grey debris matching the player X-wing. */
export const TEAMMATE_DEBRIS_PALETTE = [
  0xf2f2f0, 0xe8e6e1, 0xb0b4ba, 0x7a8088, 0x4a4f57, 0xc62828, 0x8e1b1b, 0xffd54f,
] as const;

/** Max lateral jump when picking a new roam point (prevents teleport-looking snaps). */
export const ROAM_STEP_MAX = 12;
